import axios from 'axios';

// Get API endpoint from environment variable or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// Campaign APIs
export const getCampaigns = async () => {
  const response = await api.get('/campaigns');
  return response.data;
};

export const getCampaign = async (id, includeStats = true) => {
  const response = await api.get(`/campaigns/${id}`, {
    params: { include_stats: includeStats }
  });
  return response.data;
};

export const createCampaign = async (data) => {
  const response = await api.post('/campaigns', data);
  return response.data;
};

export const updateCampaign = async (id, data) => {
  const response = await api.put(`/campaigns/${id}`, data);
  return response.data;
};

export const addRecipient = async (campaignId, data) => {
  const response = await api.post(`/campaigns/${campaignId}/recipients`, data);
  return response.data;
};

export const addRecipientsBulk = async (campaignId, recipients) => {
  const response = await api.post(`/campaigns/${campaignId}/recipients/bulk`, {
    recipients
  });
  return response.data;
};

export const getRecipients = async (campaignId) => {
  const response = await api.get(`/campaigns/${campaignId}/recipients`);
  return response.data;
};

// Get presigned S3 URL for XLSX upload
export const getUploadUrl = async (campaignId) => {
  const response = await api.get(`/campaigns/${campaignId}/upload-url`);
  return response.data;
};

// Upload XLSX file to S3 using presigned URL
export const uploadXlsxFile = async (presignedUrl, file) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  return response;
};

// Run campaign - import recipients and send emails
export const runCampaign = async (campaignId, s3Key) => {
  const response = await api.post(`/campaigns/${campaignId}/run`, {
    s3_key: s3Key
  });
  return response.data;
};

export default api;
